import * as React from "react";

export type ChipShape = "pill" | "square";
export type ChipTone =
  | "default"
  | "warning"
  | "info"
  | "success"
  | "destructive";

export interface ChipProps {
  /** Selected/pressed state. Toggles bg-primary highlight. */
  active?: boolean;
  /** Click handler. Chip is always interactive (button element). */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** "pill" (default): rounded-full. "square": rounded. */
  shape?: ChipShape;
  /** Inactive-state tone hint. Active state always uses primary regardless. */
  tone?: ChipTone;
  /** Optional count rendered as muted suffix. Only renders when count != null. */
  count?: number;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  children: React.ReactNode;
}

const BASE =
  "inline-flex items-center px-3 py-1 text-xs border transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background";

const SHAPE: Record<ChipShape, string> = {
  pill: "rounded-full",
  square: "rounded",
};

const ACTIVE = "bg-primary text-primary-foreground border-primary";

const INACTIVE_TONE: Record<ChipTone, string> = {
  default:
    "bg-card text-muted-foreground border-border hover:bg-muted/50",
  warning:
    "bg-card border-warning-soft text-warning-soft-foreground hover:bg-warning-soft/30",
  info:
    "bg-card border-info-soft text-info-soft-foreground hover:bg-info-soft/30",
  success:
    "bg-card border-success-soft text-success-soft-foreground hover:bg-success-soft/30",
  destructive:
    "bg-card border-destructive-soft text-destructive-soft-foreground hover:bg-destructive-soft/30",
};

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  function Chip(
    {
      active = false,
      onClick,
      shape = "pill",
      tone = "default",
      count,
      disabled = false,
      className,
      "aria-label": ariaLabel,
      children,
    },
    ref,
  ) {
    const composedClasses = [
      BASE,
      SHAPE[shape],
      active ? ACTIVE : INACTIVE_TONE[tone],
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={active}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        className={composedClasses}
      >
        {children}
        {count != null && (
          <span
            className={`ml-1.5 text-[inherit] ${
              active ? "opacity-80" : "opacity-70"
            }`}
          >
            {count}
          </span>
        )}
      </button>
    );
  },
);
